import * as React from "react";
import * as S from "@styled";
import ContainerWithLoadingSpinner from "components/shared/ContainerWithLoadingSpinner";
import Text from "components/shared/Text";
import Table from "components/shared/Table";
import Link from "components/shared/Link";
import EditUserModal from "./EditUserModal";

import {
    OrganizationUserRole,
    User,
    UserRole
} from "model/User";

interface GlobalUserTableProps {
    users?: User[];
    loadingUsers: boolean;
    filter?: GlobalUsersTableFilter;
    canToggleUserStatus?: boolean;
}

const GetPortalRole = (user: User) => {
    return user.roles.some(role => role.roleName === UserRole.TenantAdmin)
        ? UserRole.TenantAdmin
        : UserRole.User;
};

const GlobalUsersTable: React.FC<GlobalUserTableProps> = (props) => {

    const shownUsers = React.useMemo(() => {
        if (!props.users) {
            return [];
        }

        if (!props.filter) {
            return props.users;
        }

        return props.users.filter(
            (u) => u[props.filter!.key] === props.filter!.value,
        );
    }, [props.users, props.filter]);

    const [
        editUserModalOpen,
        setEditUserModalOpen,
    ] = React.useState<User>();

    const handleEditUserClick = (user: User) => {
        setEditUserModalOpen(user);
    };

    const shownUsersLength = shownUsers?.length ?? 0;

    return (
        <ContainerWithLoadingSpinner loading={false}>
            {!props.loadingUsers && shownUsersLength > 0 && (
                <Table>
                    <table>
                        <S.SettingsTableHeadSROnly>
                            <tr>
                                <th>Name</th>
                                <th>Role</th>
                                <th>Login type</th>
                                <th>Organization</th>
                                <th>Disabled</th>
                                <th>Actions</th>
                            </tr>
                        </S.SettingsTableHeadSROnly>

                        <tbody>
                            {shownUsers.map((user, id) => (
                                <S.GlobalUsersTableRow key={id}>
                                    <td>
                                        <Text
                                            tag="span"
                                            color={
                                                user.disabled ? "grey" : "black"
                                            }
                                        >{user.name}
                                        </Text>
                                    </td>
                                    <td>
                                        <SingleRoleDataCell
                                            roles={user.roles}
                                            disabled={user.disabled}
                                        />
                                    </td>
                                    <td>
                                        <MultiOrganizationDataCell
                                            roles={user.roles}
                                            disabled={user.disabled}
                                        />
                                    </td>
                                    <td>
                                        <Text
                                            tag="span"
                                            color={
                                                user.disabled ? "grey" : "black"
                                            }
                                        >
                                            {user.loginType}
                                        </Text>
                                    </td>
                                    <td>
                                        <Text tag="span" color="grey">
                                            {user.disabled ? "Disabled" : ""}
                                        </Text>
                                    </td>
                                    <td>
                                        <S.UsersTableActionButtonsContainer>
                                            <Link
                                                type="button"
                                                onClick={() =>
                                                    handleEditUserClick(user)
                                                }
                                            >Edit
                                            </Link>
                                            
                                        </S.UsersTableActionButtonsContainer>
                                    </td>
                                </S.GlobalUsersTableRow>
                            ))}
                        </tbody>
                    </table>
                </Table>
            )}

            {!props.loadingUsers && shownUsersLength === 0 && (
                <Text tag="p">No users</Text>
            )}
            
            {editUserModalOpen !== undefined && (
                <EditUserModal
                    open={editUserModalOpen !== undefined}
                    title="Edit User"
                    user={editUserModalOpen}
                    portalRole={GetPortalRole(editUserModalOpen)}
                    canSelectOrganizations={false}
                    onClose={() =>
                        setEditUserModalOpen(undefined)
                    }
                    onUserEdited={() => setEditUserModalOpen(undefined)}
                />
            )}
        </ContainerWithLoadingSpinner>
    );
};

interface SingleRoleDataCellProps {
    roles: OrganizationUserRole[];
    disabled: boolean;
}

const SingleRoleDataCell: React.FC<SingleRoleDataCellProps> = (props : User) => {
    const GetDisplayName = (role: UserRole) => {
        return (role === UserRole.TenantAdmin)
            ? "Portal Admin"
            : role;
    };

    return (
        <Text tag="span" color={props.disabled ? "grey" : "black"}>
            {GetDisplayName(GetPortalRole(props))}
        </Text>
    );
};

interface MultiOrganizationDataCellProps {
    roles: OrganizationUserRole[];
    disabled: boolean;
}

const MultiOrganizationDataCell: React.FC<MultiOrganizationDataCellProps> = (props) => {
    const commaSeparatedOrganizationRoles = props.roles
        .map((r) => `${r.organizationName}`)
        .join(", ");
    return <Text color={props.disabled ? "grey" : "black"}>
        {commaSeparatedOrganizationRoles}
    </ Text>;
};

export default GlobalUsersTable;

type UserObjectKey = keyof User;

export interface GlobalUsersTableFilter {
    key: UserObjectKey;
    value: User[UserObjectKey];
}
